import { ref } from 'vue'
import { FILE_VALIDATION, FORM_MESSAGES } from '@/constants/providerOptions.js'

export function useProviderThumbnail() {
  // Thumbnail upload state
  const thumbnailFile = ref(null)
  const thumbnailPreview = ref(null)
  const isUploadingThumbnail = ref(false)

  // File validation
  const validateFile = (file) => {
    const errors = []

    // Validate file type
    if (!FILE_VALIDATION.ACCEPTED_TYPES.includes(file.type)) {
      errors.push(FORM_MESSAGES.INVALID_FILE_TYPE)
    }

    // Validate file size
    if (file.size > FILE_VALIDATION.MAX_SIZE_BYTES) {
      errors.push(FORM_MESSAGES.FILE_TOO_LARGE)
    }

    return errors
  }

  // Handle thumbnail file selection
  const handleThumbnailChange = (event, onError) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file
    const validationErrors = validateFile(file)
    if (validationErrors.length > 0) {
      if (onError) {
        onError(validationErrors[0])
      }
      return
    }

    thumbnailFile.value = file

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      thumbnailPreview.value = e.target.result
    }
    reader.readAsDataURL(file)

    // Clear any previous errors
    if (onError) {
      onError(null)
    }
  }

  // Remove thumbnail
  // If providerName is provided, it's an existing provider - we need to call API
  // If not provided, it's just a local preview clear
  const removeThumbnail = async (providerName = null, onSuccess = null, onError = null) => {
    // If this is an existing provider with a thumbnail, call DELETE API
    if (providerName && thumbnailPreview.value) {
      try {
        isUploadingThumbnail.value = true
        const Provider = (await import('@/api/Provider')).default
        const result = await Provider.deleteThumbnail(providerName)

        if (result.status === 'success') {
          // Successfully deleted from backend
          thumbnailFile.value = null
          thumbnailPreview.value = null

          if (onSuccess) {
            onSuccess()
          }
        } else {
          // API call failed
          if (onError) {
            onError(result.message || 'Failed to remove thumbnail')
          }
        }
      } catch (error) {
        console.error('Error removing thumbnail:', error)
        if (onError) {
          onError('Failed to remove thumbnail')
        }
      } finally {
        isUploadingThumbnail.value = false
      }
    } else {
      // Just clear local state (new upload preview)
      thumbnailFile.value = null
      thumbnailPreview.value = null
    }

    // Reset file input in both cases
    const fileInput = document.getElementById('thumbnailInput')
    if (fileInput) fileInput.value = ''
  }

  // Set existing thumbnail URL as preview (for edit mode)
  const setExistingThumbnail = (thumbnailUrl) => {
    if (thumbnailUrl) {
      thumbnailPreview.value = thumbnailUrl
      thumbnailFile.value = null // Clear file since this is an existing URL
    }
  }

  // Reset thumbnail state
  const resetThumbnail = () => {
    thumbnailFile.value = null
    thumbnailPreview.value = null
    isUploadingThumbnail.value = false
  }

  return {
    // State
    thumbnailFile,
    thumbnailPreview,
    isUploadingThumbnail,

    // Methods
    validateFile,
    handleThumbnailChange,
    removeThumbnail,
    setExistingThumbnail,
    resetThumbnail
  }
}